/**
 * Mobile Bulk Add Banner - Free Agent Search
 * Shows when bulk mode is enabled and players are selected
 */

import React from 'react';
import { Users, CheckCircle } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../../services/colorService';
import { useMobileBatchSelection } from './MobileBatchSelectionProvider';

const C = dynastyTheme.tokens.colors;

const MobileBulkAddBanner = ({ rosterCapacityAnalysis, onBatchAdd }) => {
  const { selectionStats, clearAllSelections } = useMobileBatchSelection();

  if (!selectionStats.hasSelections) {
    return null;
  }

  const availableSlots = rosterCapacityAnalysis?.availableSlots || 0;
  const canAdd = selectionStats.count <= availableSlots;

  return (
    <div style={{
      position: 'fixed',
      top: '60px',
      left: 0,
      right: 0,
      zIndex: 100,
      padding: '12px 15px',
      backgroundColor: canAdd
        ? dynastyTheme.tokens.colors.primary
        : `${dynastyTheme.tokens.colors.error}dd`,
      backdropFilter: 'blur(10px)',
      boxShadow: `0 4px 12px ${withAlpha(C.black, 0.3)}`,
      borderBottom: `2px solid ${canAdd ? dynastyTheme.tokens.colors.warning : dynastyTheme.tokens.colors.error}`
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {canAdd ? (
            <CheckCircle size={20} style={{ color: C.black }} />
          ) : (
            <Users size={20} style={{ color: C.white }} />
          )}
          <div>
            <p style={{
              fontSize: '13px',
              fontWeight: '700',
              color: canAdd ? C.black : C.white,
              margin: 0
            }}>
              {selectionStats.count} player{selectionStats.count !== 1 ? 's' : ''} selected
            </p>
            <p style={{
              fontSize: '10px',
              color: canAdd ? C.black : C.white,
              margin: 0,
              opacity: 0.8
            }}>
              {availableSlots} roster slot{availableSlots !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>
        <button
          onClick={clearAllSelections}
          style={{
            padding: '4px 10px',
            backgroundColor: canAdd ? withAlpha(C.black, 0.1) : withAlpha(C.white, 0.2),
            color: canAdd ? C.black : C.white,
            border: 'none',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '600',
            cursor: 'pointer',
            textTransform: 'uppercase'
          }}
        >
          Clear
        </button>
      </div>

      <button
        onClick={onBatchAdd}
        disabled={!canAdd}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: canAdd ? C.black : withAlpha(C.white, 0.2),
          color: canAdd ? dynastyTheme.tokens.colors.primary : C.white,
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: canAdd ? 'pointer' : 'not-allowed',
          opacity: canAdd ? 1 : 0.6,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {canAdd
          ? `Assign Positions to ${selectionStats.count} Player${selectionStats.count !== 1 ? 's' : ''}`
          : `Not Enough Roster Slots (Need ${selectionStats.count - availableSlots} More)`
        }
      </button>
    </div>
  );
};

export default MobileBulkAddBanner;
